import React, { useState, useContext, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { gql, useQuery, useMutation } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import Spinner from "../../components/Spinner";
import DeviceProfilesCardView from "./DeviceProfilesCardView";
import DeviceProfilesListingView from "./DeviceProfilesListingView";
import AddDeviceProfileSidebar from "./AddDeviceProfileSidebar";
import AddDeviceProfileWizard from "./AddDeviceProfileWizard";
import EntityOperationBar from "../../components/EntityOperationBar";
import ConfirmModal from "../../components/modals/ConfirmModal";
import "../../starek.css";

// UI Strings
const UI_STRINGS = {
    PAGE_TITLE: "Device Profiles",
    SEARCH_PLACEHOLDER: "Search",
    VIEW_MODE_CARD: "Card View",
    VIEW_MODE_LIST: "List View",
};

// Confirmation messages
const PROFILE_MESSAGES = {
    DELETE_PROFILES_BULK: (
        count
    ) => `You are about to delete ${count} profile(s).

This action cannot be undone.

Are you sure you wish to continue?`,
};

const GET_DEVICE_PROFILES = gql`
    query GetDeviceProfiles($plant_id: Int!) {
        deviceProfiles(where: { plant_id: { equals: $plant_id } }) {
            id
            name
            description
            configuration
            plant_id
            created_at
            updated_at
            created_by {
                id
                name
            }
            updated_by {
                id
                name
            }
        }
    }
`;

const DELETE_DEVICE_PROFILE = gql`
    mutation DeleteDeviceProfile($id: Int!) {
        deleteOneDeviceProfile(where: { id: $id }) {
            id
        }
    }
`;

const UPDATE_DEVICE_PROFILE = gql`
    mutation UpdateDeviceProfile($id: Int!, $configuration: JSON!) {
        updateOneDeviceProfile(
            where: { id: $id }
            data: { configuration: $configuration }
        ) {
            id
            configuration
        }
    }
`;

const DeviceProfiles = () => {
    const context = useContext(StarekContext);

    const { data, loading, refetch } = useQuery(GET_DEVICE_PROFILES, {
        variables: { plant_id: context?.lastUsedPlant?.id },
        skip: !context?.lastUsedPlant?.id,
    });

    const [deleteDeviceProfile] = useMutation(DELETE_DEVICE_PROFILE, {
        client: context.engineClient,
        onCompleted: () => {
            context.showInfo("Device Profile deleted successfully");
            refetch();
        },
        onError: (error) => {
            context.showError(
                error.message || "Failed to delete Device Profile"
            );
        },
    });

    const [updateDeviceProfile] = useMutation(UPDATE_DEVICE_PROFILE, {
        client: context.engineClient,
        onCompleted: () => {
            context.showInfo(
                "Device Profile configuration updated successfully"
            );
            refetch();
        },
        onError: (error) => {
            context.showError(
                error.message || "Failed to update Device Profile configuration"
            );
        },
    });

    const [showAddSidebar, setShowAddSidebar] = useState(false);
    const [showWizard, setShowWizard] = useState(false);
    const [sidebarFormData, setSidebarFormData] = useState(null);
    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [viewMode, setViewMode] = useState("card"); // "card" or "list"
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);

    const deviceProfiles = data?.deviceProfiles ?? [];

    const filteredProfiles = useMemo(() => {
        const q = filterText.trim().toLowerCase();
        if (!q) return deviceProfiles;
        return deviceProfiles.filter(
            (item) =>
                item.name?.toLowerCase().includes(q) ||
                item.description?.toLowerCase().includes(q)
        );
    }, [deviceProfiles, filterText]);

    const handleAdd = () => {
        setShowAddSidebar(true);
    };

    const handleEdit = () => {
        if (selectedRows.length !== 1) return;
        setShowAddSidebar(true);
    };

    const handleDrawerClose = async () => {
        setShowAddSidebar(false);
        setSelectedRows([]);
        setSidebarFormData(null);
        await refetch();
    };

    const handleBulkDelete = async () => {
        setIsDeleting(true);
        setDeleteDone(0);
        try {
            for (const profile of selectedRows) {
                await deleteDeviceProfile({
                    variables: { id: profile.id },
                });
                setDeleteDone((n) => n + 1);
            }
            setSelectedRows([]);
            await refetch();
        } catch (error) {
            context.showError(error.message || "Failed to delete profiles");
        } finally {
            setIsDeleting(false);
            setDeleteDone(0);
            setConfirmCfg({ open: false });
        }
    };

    const handleOpenWizard = (formData) => {
        setSidebarFormData(formData);
        setShowWizard(true);
    };

    if (loading) {
        return (
            <div
                className="d-flex flex-column justify-content-center align-items-center"
                style={{ minHeight: "100vh" }}
            >
                <div className="text-xs d-flex justify-content-center">
                    <i className="fa fa-sd-card fa-10x text-primary"></i>
                </div>
                <Spinner />
            </div>
        );
    }

    return (
        <div className="container-fluid">
            {deviceProfiles && (
                <>
                    <div className="font-weight-bold mb-3">
                        {UI_STRINGS.PAGE_TITLE}
                    </div>

                    <EntityOperationBar
                        entity={{
                            singular: "Device Profile",
                            plural: "Device Profiles",
                        }}
                        allowSearch={true}
                        allowAdd={true}
                        allowDelete={true}
                        allowEdit={true}
                        selectedRows={selectedRows}
                        isDeleting={isDeleting}
                        deleteDone={deleteDone}
                        onSearch={(text) => setFilterText(text)}
                        onAdd={handleAdd}
                        onViewChange={(mode) => {
                            setViewMode(mode);
                            setSelectedRows([]);
                        }}
                        onEdit={handleEdit}
                        onDelete={handleBulkDelete}
                    >
                        <EntityOperationBar.View
                            mode="card"
                            icon="fa fa-th"
                            tooltip={UI_STRINGS.VIEW_MODE_CARD}
                        >
                            <DeviceProfilesCardView
                                profiles={filteredProfiles}
                                selectedRows={selectedRows}
                                onSelectedRowsChange={({ selectedRows }) =>
                                    setSelectedRows(selectedRows)
                                }
                                onEdit={(profile) => {
                                    setSelectedRows([profile]);
                                    setShowAddSidebar(true);
                                }}
                                onDelete={handleBulkDelete}
                                onConfigure={(profile) => {
                                    setSelectedRows([profile]);
                                    setShowWizard(true);
                                }}
                            />
                        </EntityOperationBar.View>

                        <EntityOperationBar.View
                            mode="list"
                            icon="fa fa-list"
                            tooltip={UI_STRINGS.VIEW_MODE_LIST}
                        >
                            <DeviceProfilesListingView
                                profiles={filteredProfiles}
                                selectedRows={selectedRows}
                                onSelectedRowsChange={({ selectedRows }) =>
                                    setSelectedRows(selectedRows)
                                }
                                onEdit={(profile) => {
                                    setSelectedRows([profile]);
                                    setShowAddSidebar(true);
                                }}
                                onDelete={handleBulkDelete}
                                onConfigure={(profile) => {
                                    setSelectedRows([profile]);
                                    setShowWizard(true);
                                }}
                            />
                        </EntityOperationBar.View>
                    </EntityOperationBar>
                </>
            )}

            {/* Sidebar */}
            {showAddSidebar && (
                <AddDeviceProfileSidebar
                    profile={selectedRows[0] || null}
                    onClose={handleDrawerClose}
                    onOpenWizard={handleOpenWizard}
                />
            )}

            {/* Wizard Modal */}
            {showWizard && (
                <AddDeviceProfileWizard
                    modal={true}
                    profile={selectedRows[0] || null}
                    initialConfig={sidebarFormData?.configuration}
                    onClose={() => {
                        setShowWizard(false);
                        setSidebarFormData(null);
                    }}
                    onFinish={async (manifest) => {
                        console.log("=== DeviceProfiles: Wizard finished ===");
                        console.log("Received manifest:", manifest);
                        console.log("=======================================");

                        // Update the device profile configuration
                        if (selectedRows[0]?.id) {
                            await updateDeviceProfile({
                                variables: {
                                    id: selectedRows[0].id,
                                    configuration: manifest,
                                },
                            });
                        }

                        setShowWizard(false);
                    }}
                />
            )}

            <ConfirmModal
                open={confirmCfg.open}
                title={confirmCfg.title}
                message={confirmCfg.message}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                type={confirmCfg.type}
                loading={isDeleting}
                count={selectedRows.length}
                done={deleteDone}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            />
        </div>
    );
};

export { DeviceProfiles };
